export const DEFAULT_API_ERROR_MESSAGE = 'API request failed';

export type ApiResponseError = {
  attribute?: string;
  code?: string;
  detail?: string;
  pointer?: string;
};

export interface V3ErrorResponse {
  detail?: string;
  errors: ApiResponseError[];
}

export const buildV3Error = (
  ...errors: Array<string | ApiResponseError>
): V3ErrorResponse => ({
  errors: errors.map((e) => (typeof e === 'string' ? { detail: e } : e)),
});

/**
 * Error class for API request failures.
 *
 * Contains the original Response object for access to status, headers, etc.
 * Also carries structured errors and the parsed JSON body (since the response
 * body has already been read) when available.
 */
export class ApiError extends Error {
  response: Response;
  errors: ApiResponseError[];
  body: unknown;
  type?: string;

  constructor(
    message: string,
    response: Response,
    errors: ApiResponseError[] = [],
    body?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
    this.response = response;
    this.errors = errors;
    this.body = body;
    this.type =
      body &&
      typeof body === 'object' &&
      'type' in body &&
      typeof body.type === 'string'
        ? body.type
        : undefined;
  }
}

export type ApiValidations = Record<string, string[]>;

/**
 * Field-level validations from a `RailsApiFormat::Error` body:
 * `{ errors: { validations: { field: [messages] } } }`.
 */
export const getApiValidations = (
  error: unknown
): ApiValidations | undefined => {
  if (!(error instanceof ApiError)) return undefined;

  const body = error.body as { errors?: { validations?: ApiValidations } };
  return body?.errors?.validations;
};

/**
 * The server's error message when the response carried one, otherwise
 * `fallback`.
 */
export const getApiErrorMessage = (error: unknown, fallback: string) =>
  error instanceof ApiError && error.message !== DEFAULT_API_ERROR_MESSAGE
    ? error.message
    : fallback;
